import { gql } from '@apollo/client';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { isMobileOnly } from 'react-device-detect';
import styled from 'styled-components';
import { FOOTER_QUERY } from '../../components/footer';
import { DarkFooter } from '../../components/footer/darkFooter';
import { HEADER_QUERY, Header } from '../../components/header';
import LegalMobileMenu from '../../components/legal/mobileMenu';
import { Col, ResponsiveContainer, Row } from '../../components/responsive';
import { ContentStackClient } from '../../lib/contentstack';
import { AllPrivacyAndLegal, PrivacyAndLegal } from '../../types/contentstack';
import { IPageProps } from '../../types/page';
import { classnames } from '../../utils/classnames';
import { mediaScreen } from '../../utils/mediaScreen';

export const Page: NextPage<
  IPageProps<PrivacyAndLegal> & { leftMenuItems: PrivacyAndLegal[] }
> = ({ header, page, footer, leftMenuItems }) => {
  return (
    <>
      <Header
        pageTitle={page?.title}
        disableHiding={!isMobileOnly}
        {...header}
      />

      <Container>
        <Row>
          <Col xs={12} md={3}>
            <LegalMobileMenu
              url={page?.url || ''}
              leftMenuItems={leftMenuItems}
            />

            <LeftMenu>
              {leftMenuItems?.map((item, index) => (
                <Link href={item.url as string} key={`left-menu-item-${index}`}>
                  <span
                    className={classnames({
                      link: true,
                      active: item.url === page.url,
                    })}
                  >
                    {item.title}
                  </span>
                </Link>
              ))}
            </LeftMenu>
          </Col>

          <Col xs={12} md={6}>
            <Title>{page?.title}</Title>

            <Content
              dangerouslySetInnerHTML={{ __html: page?.content as string }}
            />
          </Col>
        </Row>
      </Container>

      <DarkFooter links={footer?.links} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const data = await fetchLegalData();

  const page = data?.page.items?.find((page) =>
    page?.url?.includes(params?.id?.[0] as string)
  );

  const leftMenuItems =
    data?.page?.items?.map((item) => ({
      title: item?.title,
      url: item?.url,
    })) ?? [];

  if (!page) {
    return {
      props: {},
      redirect: {
        statusCode: 302,
        destination: leftMenuItems[0]?.url as string,
      },
    };
  }

  return {
    props: {
      ...data,
      leftMenuItems,
      page,
      path: params?.id,
    },
  };
};

export async function fetchLegalData() {
  try {
    const { data } = await ContentStackClient().query<
      IPageProps<AllPrivacyAndLegal>
    >({
      query: PAGES_QUERY,
    });

    return data;
  } catch (e) {
    return;
  }
}

const PAGES_QUERY = gql`
  query privacyAndLegalQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: all_privacy_and_legal {
      items {
        url
        title
        content
      }
    }
  }
`;

const Container = styled(ResponsiveContainer)`
  margin-top: 150px;
`;

const LeftMenu = styled.div`
  position: fixed;

  ${mediaScreen('xs', 'md')} {
    display: none;
  }

  .link {
    color: white;
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 15px;
    display: block;

    &:hover {
      text-decoration: underline;
    }

    &.active {
      font-weight: bold;
      color: ${(props) => props.theme.colors.teal};
    }
  }
`;

const Title = styled.h1`
  font-size: 50px;
  color: white;
  font-weight: bold;
  margin-bottom: 35px;
  line-height: 64px;
  margin-top: -5px;
`;

const Content = styled.div`
  text-align: justify;

  * {
    font-size: 15px !important;
    font-family: inherit !important;
    color: white !important;
    background: none !important;
  }

  h1,
  h2,
  p {
    font-weight: 300;
    margin: 20px 0;
  }

  span > font {
    display: inline-block;
    width: 30px;
    overflow: hidden;
  }

  table {
    width: 100%;
    border-collapse: collapse !important;

    td {
      vertical-align: middle;
      text-align: center;
      padding: 10px !important;
      border-color: rgba(255, 255, 255, 0.4) !important;

      p {
        margin: 0;
      }
    }
  }
`;

export default Page;
